// @ts-strict-ignore
import {Component, PanelLifecycleContext} from 'panel';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import {nanoid} from 'nanoid';

import {colors, getChartSegmentColorCombo} from 'common/util/colors';
import {
  assignOrdinalNumbersToSegmentKeys,
  isPublicMode,
  isUrlDefinedExperimentalFeatureEnabled,
} from 'common/report/util';
import {ProjectFeatureFlag} from 'common/report/constants';
import {ColumnGroup, Columns, TableColumn, TableRow} from 'common/components/data-table/types';
import {LegendOverride, LegendOverrides, SavedLegend, SavedLegendItem, SortConfig} from 'common/types/reports/bookmark';
import {createRowCompareFunc} from 'common/report/util/sorting/table-sorting';
import {Subscribable} from 'common/types/context/subscribable';
import type {ThemesResource} from 'common/report/model-resources/themes';
import {Theme} from 'common/report/queries/app-api/themes';
import {getColorsForMode, resolveTheme} from 'common/query-builder/chart-customization/colors/utils';
import {STATIC_THEMES} from 'common/query-builder/chart-customization/colors/constants';
import {getLocation} from 'common/util/url';

import {getSeriesMatchPath, mergeLegendOverrides} from './utils';

export interface SegmentItem {
  id: string;
  label: string;
  // support raw color strings to match any color the chart segments use, the chart segments are assumed to use the correct colors
  color?: string;
  // a list of additional segments per section to select when toggled
  relatedSegmentsPerSection?: string[][];

  // associated table row for this item. used in mp-segment-filter-data-table
  tableRow?: TableRow;
  // Note: if any item has a comparisonTableRow, all items are expected to have a comparisonTableRow (same if any item doesn't have a comparisonTableRow),
  // this is a quirk/limitation with how the underlying Data Table widget handles comparison rows
  comparisonTableRow?: TableRow;
}

export type ReadOnlySegmentItem = Pick<SegmentItem, `id` | `label` | `color`>;

export interface SegmentSection {
  label?: string;
  items: SegmentItem[];

  // items that are always present for display purposes, non-interactive
  readOnlyItems?: ReadOnlySegmentItem[];

  // useful if you expect to getSections() manually to implement custom logic
  metadata?: any;
  /*
  Does this segment section have more data than is shown on the client side?
  */
  apiResultsTruncated?: boolean;
  /*
  Goes along with `apiResultsTruncated`; when the limited segments don't represent the "top" of available data, this suppresses labeling as "12 of top 100"
  */
  suppressShowingTop?: boolean;

  // associated data table columns for this group of items
  tableColumns?: Columns;
  // optional value formatter for data cells in the data table
  tableValueFormatter?: any;
}

export type HoveredItem = {
  /**
   * Specifies a results path
   * @example `A. Viewed Report, Chrome, USA`
   */
  seriesId: string | null;
  /**
   * Defines how the hovered item will match results in other components.
   * Within the context of a report we want exact matches, however when hovering items on
   * a board, we want similar (not exact) results to be highlighted
   */
  partialMatch?: boolean;
  /** Defines the id of the SegmentFilterController initiating the hover interaction */
  sourceId: string;
  /** Defines the name of the component triggering an update, this is used to prevent double update cycles */
  source?: string;
};

/**
 * SelectMode controls how segments are selected in the data table.
 *
 * SelectMode.Position means that when you change sort order, it'll change segment selections to select top segments
 * based on the new sort order. You only enter SelectMode.Position by changing the segments returned by the query
 * (e.g. add/removing a breakdown). When loading a bookmark, you start in SelectMode.Items (i.e. the selected segments
 * from the bookmark are used). And if you click "Select Top Segments" or select individual segments, you'll enter
 * SelectMode.Items.
 */
export enum SelectMode {
  Item = `items`, // User selected segments
  Position = `positional`, // The top N segments
}

function getSegmentIdsPerSection(sections: SegmentSection[]): string[][] {
  return sections.map((section) => section.items.map((item) => item.id));
}

function mapToSet(arr: string[][]): Array<Set<string>> {
  return arr.map((subarr) => new Set(subarr));
}

// Note: this manual equality check is significantly faster than calling lodash/isEqual on sections
function sectionSetEquals(sections1: Array<Set<string>>, sections2: Array<Set<string>>): boolean {
  if (sections1?.length !== sections2?.length) {
    return false;
  }
  for (let sectionIdx = 0; sectionIdx < sections1.length; sectionIdx++) {
    const set1 = sections1[sectionIdx];
    const set2 = sections2[sectionIdx];
    if (set1.size !== set2.size) {
      return false;
    }
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }
  }
  return true;
}

function sectionsContainSavedLegend(sections: Array<Set<string>>, savedLegend: Array<Set<string>>): boolean {
  if (sections?.length !== savedLegend?.length) {
    return false;
  }
  for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
    const section = sections[sectionIdx];
    const saved = savedLegend[sectionIdx];
    for (const item of saved) {
      if (!section.has(item)) {
        return false;
      }
    }
  }
  return true;
}

export function getItemsSortedByTableRow(section: SegmentSection, tableSortConfig: SortConfig): SegmentItem[] {
  if (!Array.isArray(section.tableColumns)) {
    throw new Error(`Segment section must have table columns`);
  }
  if (section.items.some((item) => !item.tableRow)) {
    throw new Error(`Segment items must have table rows`);
  }

  const compareFunc = createRowCompareFunc({
    sortConfig: tableSortConfig,
    columns: section.tableColumns.flatMap((column): TableColumn[] => {
      if ((column as ColumnGroup).columns) {
        return (column as ColumnGroup).columns;
      }
      return [column as TableColumn];
    }),
    rows: section.items.map((item) => item.tableRow),
  });

  return section.items.slice().sort((a, b) => {
    return compareFunc(a.tableRow, b.tableRow);
  });
}

/**
 * Generates legend from segment filter controller state
 * Use this method to convert current state to a saveable bookmark
 * @param selectedIdsPerSection
 * @param assignedColorsPerSection
 */
export function generateSavedLegend(
  selectedIdsPerSection: Array<Set<string>>,
  assignedColorsPerSection: Array<Map<string, number>>,
): SavedLegend[] {
  const legend: SavedLegend[] = [];

  for (let i = 0; i < selectedIdsPerSection.length; i++) {
    const selectedIds = Array.from(selectedIdsPerSection[i].values());
    const legendItems: SavedLegendItem[] = selectedIds.map((id) => {
      return {
        key: id,
        // TODO(reviewer) - is this logic correct? I want to make sure that we save the information correctly
        // Only save a color if it was assigned one. This shouldn't be an issue when this context was initialized
        // from a saved bookmark (since they should map to the same color persisted), but for other legends, I think
        // that some of them won't have assigned colors? just want to get another pair of eyes on this
        ...(assignedColorsPerSection[i].has(id) ? {color: assignedColorsPerSection[i].get(id)} : {}),
      };
    });

    legend.push({
      selected: legendItems,
    });
  }

  // We display legends from last breakdown -> first so it's more intuitive to the user + the more important sections are first,
  // but when saving we want them to be in the order matching the group_by
  return legend.reverse();
}

export function idsAndAssignedColorsFromLegend(savedLegend: SavedLegend[]): {
  selectedIdsPerSection: string[][];
  assignedColorsPerSection: Array<Map<string, number>>;
} {
  const idsPerSection: string[][] = [];
  const colorOverrides: Array<Map<string, number>> = [];

  for (let i = 0; i < savedLegend.length; i++) {
    const legendSection = savedLegend[i];
    const colorsForThisSection = new Map<string, number>();
    const ids: string[] = [];

    for (const item of legendSection.selected ?? []) {
      ids.push(item.key);
      // Only the final section should have color, non-final ones have default color 1
      const color = i === savedLegend.length - 1 ? item.color : 1;
      if (color) {
        colorsForThisSection.set(item.key, color);
      }
    }

    idsPerSection.push(ids);
    colorOverrides.push(colorsForThisSection);
  }

  // N.B. We reverse the sections since bookmarks save legends in the groupBy order, but we want to show the most
  // distinguishing legend section first, which is the final breakdown
  return {
    selectedIdsPerSection: idsPerSection.reverse(),
    assignedColorsPerSection: colorOverrides.reverse(),
  };
}

export const DEFAULT_SELECT_ALL_LIMIT = 1000;

export enum TrackedToggleType {
  Select = `select`,
  DeSelect = `de-select`,
  SelectAll = `select all`,
  DeSelectAll = `de-select all`,
  SelectAllWithSearch = `select all matching search`,
  DeSelectAllWithSearch = `de-select all matching search`,
  Internal = `internal`,
}

export class SegmentFilterController implements PanelLifecycleContext, Subscribable {
  public sections: SegmentSection[] | null = null;
  private selectedIdsPerSection: Array<Set<string>> = [];
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];
  private canApplyDefaults = true;
  private defaultIdsPerSection: string[][] | null = null;
  private assignedColorsPerSection: Array<Map<string, number>> = [];
  private _tableSortConfig: null | SortConfig = null;
  private declare _segmentationQueryLimit: number;
  private declare _exportSegmentationQueryLimit: number;
  /** Theme precedence: draftTheme (a theme being edited) > reportTheme (a bookmark's inline theme) > projectDefaultTheme */
  private declare draftTheme: Theme;

  /** @todo (colors) -- temporal solution to experiment with auto color assignment */
  private assignedColorsPerIds: Map<string, string> = new Map();

  private _overrides: LegendOverrides = {};
  private _defaultNumSelections = null;
  private _maxNumSelections = null;
  private _selectMode = SelectMode.Item;
  private _hoveredSeries: HoveredItem | null = null;

  public declare id: string;
  public declare themesResource: ThemesResource;
  public declare reportTheme: Theme | {id: number | string} | null;
  public defaultSortConfig: SortConfig = null;

  /** This is currently set by the ChartRoot component  */
  public handleLegendChanges: () => void = null;

  constructor() {
    this.id = nanoid();
  }

  get defaultNumSelections() {
    return this._defaultNumSelections;
  }

  get maxNumSelections() {
    return this._maxNumSelections;
  }

  public get tableSortConfig(): null | SortConfig {
    return this._tableSortConfig;
  }

  updateConnectedComponents(): void {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }

  /**
   * Clears and replaces all legend items, applies @persistedSelectionsForEachSection once if it changes from a previous value.
   * If supplied with the same value as previously, this method is a noop and does not affect the current selection set
   * @param orderedSections - sections of all available segments
   * @param options?.defaultNumSelections? - number of top segments to initially select
   * @param options?.maxNumSelections? - maximum number of segments that can be selected
   * @param options?.tableSortConfig? - sorts the segments according to the sort config if provided.
   * @param options?.defaultSortConfig? - default sorting for the data table, used for tracking and reset button in the sort menu
   *   tableSortConfig should be specified whenever the viz has an associated data table (which should be integrated
   *   using <mp-segment-filter-data-table>).
   */
  public loadSections(
    orderedSections: SegmentSection[],
    options: {
      defaultNumSelections: number;
      maxNumSelections: number;
      tableSortConfig?: SortConfig;
      defaultSortConfig?: SortConfig;
    },
  ): void {
    const oldSegmentIdsPerSection = this.sections ? getSegmentIdsPerSection(this.sections) : [];
    const segmentIdsPerSection = getSegmentIdsPerSection(orderedSections);

    // only reset selection if segments have actually changed and not just reordered
    const sectionsChanged = !sectionSetEquals(mapToSet(segmentIdsPerSection), mapToSet(oldSegmentIdsPerSection));
    this.sections = orderedSections;

    // needed for reports that use same SegmentFilter for all charts but charts have different limits
    this._defaultNumSelections = options.defaultNumSelections;
    this._maxNumSelections = options.maxNumSelections;
    this.defaultSortConfig = options.defaultSortConfig;

    const prevSelectMode = this._selectMode;
    if (options.tableSortConfig) {
      this._tableSortConfig = options.tableSortConfig;
      if (this.sections) {
        this.sections = this.sections.map((section) => {
          const sortedItems = getItemsSortedByTableRow(section, this.tableSortConfig);
          return {
            ...section,
            items: sortedItems,
          };
        });
      }
    } else {
      // Only data table legends are sortable. If `tableSortConfig` is not provided, then the legend isn't a data
      // table, SelectMode.Position doesn't make sense so we should reset to SelectMode.Item.
      this._selectMode = SelectMode.Item;
    }

    if (sectionsChanged) {
      /** reset color assignments and overrides */
      this.assignedColorsPerSection = this.sections.map(() => new Map<string, number>());
      this.assignedColorsPerIds = new Map();
      this.themesResource?.clearSourceColorMappings(this.id);

      if (this.canApplyDefaults && this.defaultIdsPerSection) {
        this.loadPersistedSelections(this.defaultIdsPerSection);
        this.canApplyDefaults = false;
      } else {
        this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
      }
    } else if (
      // When switching from viz with data table -> bar viz, we'll set `this._selectMode = Select.Item` above. However,
      // previously it might've been `SelectMode.Position`. If that's the case, we want to reset to top selections for
      // bar viz. Hence, we use `prevSelectMode` for the check here.
      prevSelectMode === SelectMode.Position
    ) {
      this.assignedColorsPerSection = this.sections.map(() => new Map<string, number>());
      this.assignedColorsPerIds = new Map();
      this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
    } else {
      this.updateConnectedComponents();
    }
  }

  public getTopSegments() {
    const segmentIdsPerSection = getSegmentIdsPerSection(this.sections);
    return [segmentIdsPerSection[0].slice(0, this.defaultNumSelections), ...segmentIdsPerSection.slice(1)];
  }

  // TODO: remove loadPersistedSelections() in favor of loadFromSavedLegend()
  public loadPersistedSelections(defaultIdsPerSection: string[][], {shouldInvokeToggleCallbacks = false} = {}) {
    if (this.canApplyDefaults && this.sections) {
      // if default selections have already been applied, persisted selections must override it everywhere so it must invoke toggle callbacks
      this.applySelections(defaultIdsPerSection, {
        shouldInvokeToggleCallbacks,
      });

      this.canApplyDefaults = false;
    } else {
      this.defaultIdsPerSection = defaultIdsPerSection;
    }
  }

  set selectMode(val: SelectMode) {
    this._selectMode = val;
  }

  get selectMode() {
    return this._selectMode;
  }

  public loadPersistedColors(assignedColors: Array<Map<string, number>>) {
    if (assignedColors && assignedColors.length) {
      this.assignedColorsPerSection = assignedColors;
      this.updateConnectedComponents();
    }
  }

  /**
   * Loads a saved legend from a bookmark, must be called *after* segments have been loaded with loadSections().
   * Note: Does *not* expect it to be reversed, the bookmark should be taken in raw from the bookmark,
   * and this method will deal with reversing it to display the last grouping first.
   * TODO: merge loadFromSavedLegend() into loadSections() because in practice they always need to be called together.
   * @param savedLegend - Legend from bookmark format
   */
  public loadFromSavedLegend(savedLegend: SavedLegend[] | null) {
    if (!savedLegend) {
      this.sections && this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
      return;
    }
    const {selectedIdsPerSection, assignedColorsPerSection} = idsAndAssignedColorsFromLegend(savedLegend);

    if (!this.sections) {
      throw new Error(`Must load available segments via loadSections() before loading saved legend.`);
    }
    const oldSegmentIdsPerSection = getSegmentIdsPerSection(this.sections);

    const savedLegendWorks = sectionsContainSavedLegend(
      mapToSet(oldSegmentIdsPerSection),
      mapToSet(selectedIdsPerSection),
    );
    this.applySelections(savedLegendWorks ? selectedIdsPerSection : this.getTopSegments(), {
      shouldInvokeToggleCallbacks: false,
    });
    this.loadPersistedColors(assignedColorsPerSection);
  }

  public toBookmarkSavedLegend(): SavedLegend[] {
    return generateSavedLegend(this.selectedIdsPerSection, this.assignedColorsPerSection);
  }

  // "shouldInvokeToggleCallbacks" should only be false when applying default or existing selections.
  // Typically, we want to invoke toggle callbacks when a selection is caused by user input.
  // In practice, apps typically subscribe to toggle callbacks to update the bookmark or URL with active selections, so in these cases we skip toggle callbacks
  // to prevent an update loop where the URL-change causes a selection-change then the selection-change causes a URL-change again.
  public applySelections(defaultIdsPerSection: string[][], {shouldInvokeToggleCallbacks = true} = {}) {
    this.selectedIdsPerSection = mapToSet(defaultIdsPerSection);
    if (shouldInvokeToggleCallbacks) {
      this.handleLegendChanges();
    }
    this.updateConnectedComponents();
  }

  public toggleItem(sectionIdx: number, id: string): void {
    const selectedIds = this.selectedIdsPerSection[sectionIdx];
    const isDeSelect = selectedIds?.has(id);

    if (isDeSelect) {
      selectedIds.delete(id);
      // remove saved color
      this.assignedColorsPerSection[sectionIdx].delete(id);
    } else {
      selectedIds.add(id);

      // select related segments
      const sectionItem = this.sections[sectionIdx].items.find((segment) => segment.id === id);
      if (sectionItem.relatedSegmentsPerSection) {
        this.selectedIdsPerSection = this.selectedIdsPerSection.map(
          (selectedIdsInSection, selectedIndex) =>
            new Set([...selectedIdsInSection, ...sectionItem.relatedSegmentsPerSection[selectedIndex]]),
        );
      }
    }
    this.updateConnectedComponents();
    this.handleLegendChanges?.();
  }

  /**
   * Gets the active hovered item in the page
   * In the context of a board, we return the globally available hovered item for all cards except
   * the card that initiated the hover. This distinction of contexts is achieved by assigning an identifying source to the hovered item
   */
  get hoveredSeries() {
    const globalItem = this.themesResource?.globalHoveredItem;

    if (globalItem && globalItem.sourceId !== this.id) {
      return globalItem;
    }

    return this._hoveredSeries;
  }

  setLegendOverride(key: string, value: LegendOverride) {
    if (value === null) {
      delete this._overrides[key];
    } else {
      this._overrides[key] = value;
    }
    this.updateConnectedComponents();
  }

  set localOverrides(overrides: LegendOverrides | undefined) {
    this._overrides = overrides ?? {};
    this.updateConnectedComponents();
  }

  /** Gets overrides local to the loaded bookmark */
  get localOverrides() {
    return this._overrides;
  }

  /**
   * Gets overrides applied to a whole board and overrides local to the loaded bookmark
   * This is primarily used for assigning colors when the user is defining a board legend color
   */
  get globalAndLocalOverrides() {
    const globalOverrides = this.themesResource?.legendOverrides;

    if (globalOverrides && Object.keys(globalOverrides).length > 0) {
      return mergeLegendOverrides(this._overrides, globalOverrides);
    }

    return this._overrides;
  }

  /** Allows data table to respond to line chart hover events and vice-versa */
  public notifySeriesHovered(hoveredItem: Omit<HoveredItem, `sourceId`> | null): void {
    const item: HoveredItem = hoveredItem ? {...hoveredItem, sourceId: this.id} : null;

    if (isEqual(item, this._hoveredSeries)) {
      return;
    }

    this._hoveredSeries = item;
    this.updateConnectedComponents();

    if (this.isBoardsColoringEnabled()) {
      // for boards, we always want to apply a partial match on results
      this.themesResource?.setGlobalHoveredItem(item ? {...item, partialMatch: true} : null);
    }
  }

  /**
   * @param ids? - If specified, will also return true if all ids in the Set are selected.
   */
  public isAllSelected(ids?: Set<string>): boolean {
    // select-all is only concerned with the first section
    return (
      this.selectedIdsPerSection[0].size === Math.min(this.sections[0].items.length, this.maxNumSelections) ||
      (Boolean(ids) && this._allIdsSelected(ids))
    );
  }

  public toggleOrderedTopItems(orderedIdsPerSection: string[][], {isSearchMode}: {isSearchMode?: boolean} = {}) {
    let toggledSelection: string[][];

    if (isSearchMode) {
      const displayedIds = new Set(orderedIdsPerSection[0]);
      if (this.isAllSelected(displayedIds)) {
        toggledSelection = [
          Array.from(this.selectedIdsPerSection[0]).filter((id) => !displayedIds.has(id)),
          ...orderedIdsPerSection.slice(1),
        ];
      } else {
        const selections = new Set(this.selectedIdsPerSection[0]);
        for (const id of orderedIdsPerSection[0]) {
          if (selections.size >= this.maxNumSelections) {
            break;
          }
          selections.add(id);
        }
        toggledSelection = [Array.from(selections), ...orderedIdsPerSection.slice(1)];
      }
    } else {
      toggledSelection = this.isAllSelected()
        ? [[], ...orderedIdsPerSection.slice(1)]
        : [orderedIdsPerSection[0].slice(0, this.maxNumSelections), ...orderedIdsPerSection.slice(1)];
    }
    this.selectedIdsPerSection = mapToSet(toggledSelection);

    this.updateConnectedComponents();
    this.handleLegendChanges?.();
  }

  public toggleAllItems(): void {
    const idsPerSection = getSegmentIdsPerSection(this.sections);
    this.toggleOrderedTopItems(idsPerSection);
  }

  public hasLoaded(): boolean {
    return this.sections !== null;
  }

  public getSections(): SegmentSection[] {
    return this.sections ?? [];
  }

  public getItems(sectionIdx: number): SegmentItem[] {
    return this.getSections()[sectionIdx]?.items ?? [];
  }

  public getDisplayedItems(sectionIdx: number): ReadOnlySegmentItem[] {
    const readOnlyItems = this.getSections()[sectionIdx]?.readOnlyItems ?? [];
    const selectedItems: ReadOnlySegmentItem[] = this.getSelectedItems(sectionIdx).map((item) =>
      pick(item, `id`, `label`, `color`),
    );
    return [...readOnlyItems, ...selectedItems];
  }

  public getSelectedItems(sectionIdx: number): SegmentItem[] {
    return this.getItems(sectionIdx).filter((item) => this.isItemSelected(sectionIdx, item.id));
  }

  public getSelectedCount(sectionIdx: number): number {
    return this.selectedIdsPerSection[sectionIdx]?.size ?? 0;
  }

  public isMaxSelected() {
    return this.getSelectedCount(0) >= this.maxNumSelections;
  }

  public isItemSelected(sectionIdx: number, id: string): boolean {
    return this.selectedIdsPerSection[sectionIdx]?.has(id) ?? false;
  }

  public set segmentationQueryLimit(limit: number) {
    this._segmentationQueryLimit = limit;
  }

  public get segmentationQueryLimit(): number {
    return this._segmentationQueryLimit;
  }

  public set exportSegmentationQueryLimit(limit: number) {
    this._exportSegmentationQueryLimit = limit;
  }

  public get exportSegmentationQueryLimit(): number {
    return this._exportSegmentationQueryLimit;
  }

  /** Apply theme to report */
  public setReportTheme(theme: Theme | {id: number | string} | null) {
    this.reportTheme = theme;
    this.updateConnectedComponents();
  }

  private getReportTheme() {
    /** for public boards, we make an assumption bookmarks will contain raw theme data or static theme ids */
    if (isPublicMode()) {
      return resolveTheme({theme: this.reportTheme});
    }

    if (!this.themesResource) {
      return STATIC_THEMES[0];
    }

    return resolveTheme({
      theme: this.reportTheme,
      themesResource: this.themesResource,
      defaultThemeId: this.themesResource?.getDefaultProjectThemeId(),
    });
  }

  /** Sets a draft theme that overrides report theme. The theme editor sets this before a theme is committed to the report */
  public setDraftTheme(theme: Theme | null) {
    this.draftTheme = theme;
    this.updateConnectedComponents();
  }

  /** searches for an exact or partial color match in the legend overrides */
  public getOverrideColor(id: string): string | null {
    return (
      this.globalAndLocalOverrides[id]?.color || this.globalAndLocalOverrides[getSeriesMatchPath(id)]?.color || null
    );
  }

  private getItemBaseColor(
    sectionIdx: number,
    itemIndex: number,
    {ignoreOverrides = true}: {ignoreOverrides?: boolean} = {},
  ) {
    const item = this.sections[sectionIdx].items[itemIndex];
    const colorOverride = this.getOverrideColor(item.id);

    // initialize default color
    let color = getChartSegmentColorCombo(itemIndex, this.getThemeColors()).primary;

    if (!ignoreOverrides && colorOverride) {
      color = colorOverride;
    }

    return color;
  }

  /**
   * @deprecated
   * Returns colors assigned by index on the bookmark legend
   * This method will be deleted after consistent colors is GA
   */
  private getItemAssignedColor(sectionIdx: number, id: string) {
    if (this.assignedColorsPerSection[sectionIdx].has(id)) {
      return getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1, this.getThemeColors())
        .primary;
    }

    // otherwise: assign new colors, and save them
    const colorAssignments = assignOrdinalNumbersToSegmentKeys(
      this.assignedColorsPerSection[sectionIdx],
      Array.from(this.selectedIdsPerSection[sectionIdx].keys()).filter(
        // Make sure we don't re-assign colors that already have them
        (key) => !this.assignedColorsPerSection[sectionIdx].has(key),
      ),
      colors.charts.default.length,
    );
    this.assignedColorsPerSection[sectionIdx] = colorAssignments;

    return getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1, this.getThemeColors())
      .primary;
  }

  // Get the overridden color, otherwise default to the supplied ones.
  // This allows widgets to opt-in to the new color overrides
  // To opt in, you can just omit `color` in the legend segments when you pass them initially to the component
  public getItemColor(
    sectionIdx: number,
    id: string,
    {ignoreOverrides = false}: {ignoreOverrides?: boolean} = {},
  ): string {
    // if it's selected, then we have to actually give it a color. If not, we dynamically determine color (unless given)
    if (this.isItemSelected(sectionIdx, id)) {
      const itemIndex = this.sections[sectionIdx].items.findIndex((segmentItem) => segmentItem.id === id);
      const item = this.sections[sectionIdx].items[itemIndex];

      // only assign colors for first section
      if (sectionIdx > 0 || item?.color) {
        return item?.color ?? null; // null means default checkbox color
      }

      if (this.isAutoConsistentColoringEnabled()) {
        // if auto coloring is enabled, this will attempt to use the same colors for each segmentation path
        // in a loaded board, so that `Chrome / USA` maps to the same color in multiple boards
        return this.assignAutoColor(sectionIdx, id);
      } else if (this.isConsistentColoringEnabled()) {
        // this will become the default behavior
        return this.getItemBaseColor(sectionIdx, itemIndex, {ignoreOverrides});
      }

      // @todo delete after consistent coloring is GA
      return this.getItemAssignedColor(sectionIdx, id);
    }
    return colors.gray1;
  }

  public getTheme() {
    return this.draftTheme || this.getReportTheme();
  }

  public getThemeColors() {
    return getColorsForMode(this.getTheme(), this.themesResource?.isDarkMode());
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }
  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  public subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((subscription) => subscription !== callback);
    return this;
  }

  private _allIdsSelected(ids: Set<string>): boolean {
    const selectedIds = new Set(this.selectedIdsPerSection[0]);
    const idsIntersectSelectedIds = [...selectedIds].filter((id) => ids.has(id));
    return idsIntersectSelectedIds.length === ids.size;
  }

  /**
   * Attempts to assign a color that is shared across a loaded board for the same segmentation path
   */
  private assignAutoColor(sectionIdx: number, itemId: string, depth = 0): string {
    const sectionItems = this.sections[sectionIdx].items;
    const itemIndex = sectionItems.findIndex((segmentItem) => segmentItem.id === itemId);
    const path = getSeriesMatchPath(itemId, {includeMetricName: false});

    // return default colors when rendering unsegmented metrics like "A. Viewed Report, B. Viewed Report"
    // since these metrics can collide in naming but differ in results
    if (path === null) {
      return this.getItemBaseColor(sectionIdx, itemIndex);
    }

    const globalColorEntry = this.themesResource?.colorMappings?.get(path);
    const colorOverride = this.getOverrideColor(itemId);

    // if the user is assigning a color override, prefer that over the globally assigned color
    if (colorOverride) {
      return colorOverride;
    } else if (this.assignedColorsPerIds.has(itemId)) {
      return this.assignedColorsPerIds.get(itemId);
    }

    let color: string;
    if (globalColorEntry) {
      color = globalColorEntry.color;

      // colors being assigned locally in an instance of SegmentFilterController are guaranteed
      // to have distinct adjacent colors since colors are allocated sequentially from a theme
      // we return early here since there are no additional checks to perform
      if (globalColorEntry.sourceId === this.id) {
        return color;
      }

      // When a color being used in another card is encountered, there is the possibility that
      // we end up showing 2 equal colors adjacent to each other, which is not a desired behavior.
      // We break this collision by picking a color that won't cause same adjacent colors
      const adjacentColors =
        depth === 0
          ? [
              itemIndex - 1 >= 0 && this.assignAutoColor(sectionIdx, sectionItems[itemIndex - 1].id, 1),
              itemIndex + 1 < sectionItems.length &&
                this.assignAutoColor(sectionIdx, sectionItems[itemIndex + 1].id, 1),
            ].filter(Boolean)
          : [];

      if (adjacentColors.includes(color)) {
        // themes are guaranteed to have at least 4 colors, for rendering distinct adjacent colors, the minium number of colors is 2
        // thus this is guaranteed to find a color that's not causing collisions, this fails safely with the initial color assignment
        color = this.getThemeColors().find((themeColor) => !adjacentColors.includes(themeColor)) ?? color;
      }

      // mark visited colors, this is used to keep track of which colors
      // have been assigned that are free of collisions
      // @todo (colors) -- ideally this will be set when `loadSections` is called
      // refactor this once we test this coloring approach
      if (depth === 0) {
        this.assignedColorsPerIds.set(itemId, color);
      }
    } else {
      // as colors are assigned, eagerly make them available for other loading cards
      color = this.getItemBaseColor(sectionIdx, itemIndex);
      this.themesResource?.setColorMapping(path, color, this.id);
    }

    return color;
  }

  public isAutoConsistentColoringEnabled() {
    return (
      !isPublicMode() &&
      (this.themesResource?.currentProject?.get()?.value?.hasFeatureFlag(ProjectFeatureFlag.AutoConsistentColors) ??
        false)
    );
  }

  public isBoardsColoringEnabled() {
    return (
      this.isConsistentColoringEnabled() &&
      isUrlDefinedExperimentalFeatureEnabled(getLocation(), `board_consistent_colors`, false)
    );
  }

  private isConsistentColoringEnabled(): boolean {
    return (
      !isPublicMode() &&
      (this.themesResource?.currentProject?.get()?.value?.hasFeatureFlag(ProjectFeatureFlag.ConsistentColors) ?? false)
    );
  }
}
